// ────────────────────────────────────────────────────────────────
// src/pages/LandingPage.js
// ────────────────────────────────────────────────────────────────

import React, { useState, useEffect } from 'react'
import { useNavigate, useOutletContext } from 'react-router-dom'
import axios from 'axios'

// GOOGLE SIGNIN (ADDED)
import { GoogleLogin } from '@react-oauth/google'

function LandingPage() {
  const navigate = useNavigate()

  // 1) Attempt to get parent's context. If none, fallback to {}
  const parentContext = useOutletContext() || {}
  // If the parent provides a lang, use that; otherwise read from localStorage
  const parentLang = parentContext.lang
  const [showLangMenu, setShowLangMenu] = useState(false)

  // 2) Local state for language. Prefer parent's lang if available, else from localStorage
  const [localLang, setLocalLang] = useState(() => {
    if (parentLang) return parentLang
    return localStorage.getItem('lang') || 'en'
  })

  // Update localStorage whenever localLang changes
  useEffect(() => {
    localStorage.setItem('lang', localLang)
  }, [localLang])

  // 3) Language labels and function to switch language locally
  const langLabel = {
    en: 'English',
    ja: '日本語',
    pt: 'Português',
  }
  const switchLanguage = (newLang) => {
    setLocalLang(newLang)
    setShowLangMenu(false)
  }

  // Form fields & error
  const [form, setForm] = useState({ email: '', password: '' })
  const [error, setError] = useState(null)

  // ⚑ NEW: loading flag while we talk to backend
  const [authLoading, setAuthLoading] = useState(false)

  // If user already logged in => go dashboard
  useEffect(() => {
    const token = localStorage.getItem('token')
    if (token) {
      navigate('/dashboard')
    }
  }, [navigate])

  // Handle changes in email/password
  const handleChange = (e) => {
    setForm({
      ...form,
      [e.target.name]: e.target.value,
    })
  }

  // Attempt login
  const handleSubmit = async (e) => {
    e.preventDefault()
    if (authLoading) return                    // ⚑ guard
    setError(null)
    setAuthLoading(true)                       // ⚑

    try {
      const res = await axios.post(
        'https://payme-backend-713827320947.asia-northeast1.run.app/api/auth/login',
        {
          email: form.email,
          password: form.password,
        }
      )
      // On success, store token & user
      const { token, user } = res.data
      localStorage.setItem('token', token)
      localStorage.setItem('user', JSON.stringify(user))

      // Navigate to dashboard
      navigate('/dashboard')
    } catch (err) {
      console.error(err)
      setError(err.response?.data?.message || t.errorLogin)
      setAuthLoading(false)                    // ⚑ reset
    }
  }

  // GOOGLE SIGNIN (ADDED) - handle success
  const handleGoogleSuccess = async (credentialResponse) => {
    if (authLoading) return                    // ⚑ guard
    try {
      setAuthLoading(true)                     // ⚑
      const { credential } = credentialResponse
      if (!credential) {
        setError('Google authentication failed')
        setAuthLoading(false)
        return
      }

      // Send to backend
      const res = await axios.post(
        'https://payme-backend-713827320947.asia-northeast1.run.app/api/auth/google-login',
        { credential }
      )
      const { token, user } = res.data
      localStorage.setItem('token', token)
      localStorage.setItem('user', JSON.stringify(user))
      navigate('/dashboard')
    } catch (err) {
      console.error(err)
      setError(err.response?.data?.message || 'Google login failed')
      setAuthLoading(false)                    // ⚑ reset
    }
  }

  // GOOGLE SIGNIN (ADDED) - handle error
  const handleGoogleError = () => {
    setError('Google Sign-In was cancelled or failed')
  }

  // 4) Language dictionary
  const translations = {
    en: {
      brandTitle: 'PayMe',
      brandDescription: 'Fast, secure, and user-friendly.\nSimplify your payment experience.',
      welcomeTitle: 'Welcome to PayMe',
      welcomeSubtitle: 'Log in to continue',
      emailLabel: 'Email',
      emailPlaceholder: 'Enter your email',
      passwordLabel: 'Password',
      passwordPlaceholder: 'Enter your password',
      loginButton: 'Log In',
      errorLogin: 'Login failed',
      noAccountQuestion: 'Don’t have an account?',
      registerLink: 'Register',
      processing: 'Processing…',            // ⚑ NEW
    },
    ja: {
      brandTitle: 'PayMe',
      brandDescription: '高速・安全・使いやすい。\n支払いをもっとカンタンに。',
      welcomeTitle: 'ペイミへようこそ',
      welcomeSubtitle: 'ログインして続行してください',
      emailLabel: 'メールアドレス',
      emailPlaceholder: 'メールアドレスを入力',
      passwordLabel: 'パスワード',
      passwordPlaceholder: 'パスワードを入力',
      loginButton: 'ログイン',
      errorLogin: 'ログインに失敗しました',
      noAccountQuestion: 'アカウントをお持ちではありませんか？',
      registerLink: '登録',
      processing: '処理中…',               // ⚑ NEW
    },
    pt: {
      brandTitle: 'PayMe',
      brandDescription:
        'Rápido, seguro e fácil de usar.\nSimplifique sua experiência de pagamento.',
      welcomeTitle: 'Bem-vindo ao PayMe',
      welcomeSubtitle: 'Faça login para continuar',
      emailLabel: 'E-mail',
      emailPlaceholder: 'Digite seu e-mail',
      passwordLabel: 'Senha',
      passwordPlaceholder: 'Digite sua senha',
      loginButton: 'Entrar',
      errorLogin: 'Falha no login',
      noAccountQuestion: 'Não tem uma conta?',
      registerLink: 'Registrar',
      processing: 'Processando…',           // ⚑ NEW
    },
  }

  // 5) pick text based on localLang
  const t = translations[localLang] || translations.en

  return (
    <div className="min-h-screen flex relative">
      {/* LEFT side image + gradient */}
      <div
        className="hidden lg:flex lg:flex-1 bg-cover bg-center relative"
        style={{
          backgroundImage:
            'url("https://themesdesign.in/tailwick/assets/images/auth/modern-login-bg.jpg")',
        }}
      >
        <div className="w-full h-full bg-gradient-to-t from-blue-800 via-purple-600 to-indigo-800 bg-opacity-60 flex flex-col items-center justify-center p-8 text-center">
          <h1 className="text-white text-4xl font-bold mb-4 tracking-wide">
            {t.brandTitle}
          </h1>
          <p className="text-white text-sm max-w-xs mx-auto leading-relaxed whitespace-pre-wrap">
            {t.brandDescription}
          </p>
        </div>
      </div>

      {/* RIGHT side: login form + language dropdown */}
      <div className="flex flex-1 flex-col justify-center px-6 py-12 bg-gray-50 relative">
        {/* Language dropdown top-right */}
        <div className="absolute top-4 right-4">
          <div className="relative inline-block text-left">
            <button
              type="button"
              onClick={() => setShowLangMenu((prev) => !prev)}
              className="inline-flex justify-center items-center gap-1 rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 focus:outline-none"
            >
              {langLabel[localLang]}
              <svg
                className="h-4 w-4 text-gray-700"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                viewBox="0 0 24 24"
                aria-hidden="true"
              >
                <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
              </svg>
            </button>

            {showLangMenu && (
              <div
                className="absolute right-0 z-10 mt-2 w-36 origin-top-right rounded-md bg-white shadow ring-1 ring-black ring-opacity-5"
              >
                <ul className="py-1 text-sm text-gray-700">
                  <li>
                    <button
                      className="block w-full text-left px-4 py-2 hover:bg-gray-100"
                      onClick={() => switchLanguage('en')}
                    >
                      English
                    </button>
                  </li>
                  <li>
                    <button
                      className="block w-full text-left px-4 py-2 hover:bg-gray-100"
                      onClick={() => switchLanguage('ja')}
                    >
                      日本語
                    </button>
                  </li>
                  <li>
                    <button
                      className="block w-full text-left px-4 py-2 hover:bg-gray-100"
                      onClick={() => switchLanguage('pt')}
                    >
                      Português
                    </button>
                  </li>
                </ul>
              </div>
            )}
          </div>
        </div>

        <div
          className={`w-full max-w-md mx-auto space-y-6 ${
            authLoading ? 'opacity-60 pointer-events-none' : ''
          }`}
        >
          {/* Add the PayMe logo here, smaller, above the heading */}
          <div className="text-center">
            <img
              src="/payme-logo.png"
              alt="PayMe"
              className="mx-auto h-32 w-auto mb-3"
            />
            <h2 className="text-2xl font-bold text-gray-700">{t.welcomeTitle}</h2>
            <p className="mt-1 text-sm text-gray-500">{t.welcomeSubtitle}</p>
          </div>

          <div className="rounded-lg shadow-lg border border-gray-200 p-6 bg-white">
            {/* Login Form */}
            <form onSubmit={handleSubmit} className="space-y-5">
              {/* Email */}
              <div>
                <label
                  htmlFor="email"
                  className="block mb-1 text-gray-700 font-medium"
                >
                  {t.emailLabel}
                </label>
                <input
                  id="email"
                  name="email"
                  type="email"
                  value={form.email}
                  onChange={handleChange}
                  required
                  disabled={authLoading}                 /* ⚑ */
                  className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring focus:ring-blue-500"
                  placeholder={t.emailPlaceholder}
                />
              </div>

              {/* Password */}
              <div>
                <label
                  htmlFor="password"
                  className="block mb-1 text-gray-700 font-medium"
                >
                  {t.passwordLabel}
                </label>
                <input
                  id="password"
                  name="password"
                  type="password"
                  value={form.password}
                  onChange={handleChange}
                  required
                  disabled={authLoading}                 /* ⚑ */
                  className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring focus:ring-blue-500"
                  placeholder={t.passwordPlaceholder}
                />
              </div>

              {/* Error display */}
              {error && (
                <p className="text-red-600 text-sm font-medium">{error}</p>
              )}

              {/* Submit button */}
              <div>
                <button
                  type="submit"
                  disabled={authLoading}               /* ⚑ */
                  className={`w-full flex items-center justify-center gap-2 bg-blue-600 text-white py-2 rounded transition-colors ${
                    authLoading
                      ? 'opacity-60 cursor-not-allowed'
                      : 'hover:bg-blue-700'
                  }`}
                >
                  {authLoading && (                        /* ⚑ spinner */
                    <svg
                      className="animate-spin h-5 w-5 text-white"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      ></circle>
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
                      ></path>
                    </svg>
                  )}
                  {authLoading ? t.processing : t.loginButton}
                </button>
              </div>
            </form>

            {/* GOOGLE SIGNIN (ADDED, make button bigger) */}
            <div className="my-4 text-center">
              <p className="text-gray-500 text-sm mb-2">OR</p>
              <div className="flex justify-center">
                {authLoading ? (                       /* ⚑ placeholder */
                  <div className="h-11 w-60 bg-gray-200 rounded animate-pulse" />
                ) : (
                  <GoogleLogin
                    onSuccess={handleGoogleSuccess}
                    onError={handleGoogleError}
                    text="continue_with"
                    size="large"          // Make button larger
                    theme="outline"       // Outline style (optional)
                    shape="rectangular"   // Rectangular shape
                  />
                )}
              </div>
            </div>
          </div>

          <div className="text-center">
            <p className="text-sm text-gray-500">
              {t.noAccountQuestion}{' '}
              <span
                onClick={() => navigate('/register')}
                className="text-blue-600 font-medium cursor-pointer hover:underline"
              >
                {t.registerLink}
              </span>
            </p>
          </div>
        </div>

        {/* ⚑ Global overlay spinner */}
        {authLoading && (
          <div className="absolute inset-0 flex flex-col items-center justify-center bg-white bg-opacity-70 z-50">
            <svg
              className="animate-spin h-10 w-10 text-blue-600 mb-3"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              ></circle>
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
              ></path>
            </svg>
            <p className="text-blue-600 font-medium">{t.processing}</p>
          </div>
        )}
      </div>
    </div>
  )
}

export default LandingPage
